import { PodhiveLogo } from "@/components/taxpeerlogo.component";
import AuthService from "@/lib/auth/AuthApi.service";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useId, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GoogleSignInButton } from "../components/signInWithGoogle.component";
import { InAppBrowserBanner } from "../components/InAppBrowserBanner.component";
import { useTranslation, Trans } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/contexts/auth.context";
import useAltchaAutoToken from "@/lib/captcha/useAltchaAutoToken.hook";
import { AltchaWidget } from "@/lib/captcha/AltchaWidget.component";
import useDeferredExecution from "@/lib/hooks/useDeferredExecution.hook";
import { AnalyticsService } from "@/lib/analytics/analytics.service";
import { captureAuthException } from "@/lib/auth/captureAuthException";

/**
 * High-conversion signup page with clear benefits and minimal friction
 */
const RegisterPage = () => {
	const { t } = useTranslation("common");
	const { setAuthToken } = useAuth();
	const [formData, setFormData] = useState({
		email: "",
		password: "",
	});
	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const navigate = useNavigate();
	const authService = AuthService.getInstance();
	const emailId = useId();
	const passwordId = useId();

	const { widgetRef, hasActiveToken, refreshNow } = useAltchaAutoToken({
		action: "register",
		intervalMs: 90_000,
	});

	const executeRegistration = useCallback(async (): Promise<void> => {
		setError(null);

		if (!formData.email || !formData.password) {
			setError(t("register.errors.fillAllFields"));
			return;
		}

		if (formData.password.length < 8) {
			setError(t("register.errors.passwordTooShort"));
			return;
		}

		setIsLoading(true);

		try {
			await authService.register({
				email: formData.email,
				password: formData.password,
			});

			const currentUser = authService.auth.currentUser;

			if (!currentUser) {
				throw new Error("Registration successful, but user could not be found");
			}

			const firebaseToken = await currentUser.getIdToken();
			const response = await authService.upgradeFirebaseIdToJWT(
				firebaseToken,
				refreshNow,
			);

			await setAuthToken(response.token);

			AnalyticsService.captureAcquisitionFromUrl();
			AnalyticsService.track("user_signed_up", { method: "email" });

			setTimeout(() => {
				navigate("/dashboard");
			}, 300);
		} catch (error: unknown) {
			captureAuthException(error, "register_page");
			if (error instanceof Error) {
				setError(error.message);
			} else {
				setError(t("register.errors.registrationFailed"));
			}
		} finally {
			setIsLoading(false);
		}
	}, [formData, authService, setAuthToken, navigate, t, refreshNow]);

	const { run: runRegistration, isBusy: isRegistrationBusy } =
		useDeferredExecution({
			condition: hasActiveToken,
			canRun: Boolean(formData.email) && Boolean(formData.password),
			timeoutMs: 15_000,
			onTimeout: () => setError(t("captcha.botDetected")),
			execute: executeRegistration,
		});

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		runRegistration();
	};

	return (
		<div className="flex min-h-screen flex-col items-center justify-center bg-card px-4 py-10 sm:py-12">
			<div className="w-full max-w-[440px] space-y-6 sm:space-y-8">
				{/* Logo */}
				<div className="flex justify-center">
					<PodhiveLogo width={125} bildmarke={false} />
				</div>

				{/* Hero */}
				<div className="space-y-3 text-center sm:space-y-4">
					{/* <div className="flex justify-center">
						<Badge variant="secondary" className="px-3 py-1 text-xs">
							<Sparkles className="mr-1.5 h-3 w-3" />
							{t("register.trustBadge")}
						</Badge>
					</div> */}

					<h1 className="text-3xl font-bold tracking-tight text-foreground">
						<Trans
							i18nKey="register.headline"
							components={{
								brand: (
									<span className="bg-gradient-to-r from-[#FFAB00] to-[#FA7A00] bg-clip-text text-transparent"></span>
								),
							}}
						/>
					</h1>
					<p className="text-muted-foreground">{t("register.subheadline")}</p>
				</div>

				<InAppBrowserBanner />

				{/* Google Sign Up */}
				<div className="space-y-3">
					<GoogleSignInButton
						text={t("register.continueWithGoogle")}
						refreshCaptcha={refreshNow}
						onSuccess={() => {
							AnalyticsService.captureAcquisitionFromUrl();
							AnalyticsService.track("user_signed_up", { method: "google" });
						}}
					/>
				</div>

				{/* Divider */}
				<div className="relative">
					<div className="absolute inset-0 flex items-center">
						<span className="w-full border-t" />
					</div>
					<div className="relative flex justify-center text-xs uppercase">
						<span className="bg-card px-2 text-muted-foreground">
							{t("or")}
						</span>
					</div>
				</div>

				{/* Email Form */}
				<form onSubmit={handleSubmit} className="space-y-5">
					{error && (
						<div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
							{error}
						</div>
					)}

					<div className="space-y-2">
						<Input
							id={emailId}
							type="email"
							value={formData.email}
							onChange={(e) =>
								setFormData((prev) => ({ ...prev, email: e.target.value }))
							}
							placeholder={t("register.emailPlaceholder")}
							disabled={isLoading}
						/>
					</div>

					<div className="space-y-2">
						<div className="relative">
							<Input
								id={passwordId}
								type={showPassword ? "text" : "password"}
								value={formData.password}
								onChange={(e) =>
									setFormData((prev) => ({
										...prev,
										password: e.target.value,
									}))
								}
								placeholder={t("register.passwordPlaceholder")}
								disabled={isLoading}
							/>
							<button
								type="button"
								className="absolute inset-y-0 right-3 text-xs text-muted-foreground hover:text-foreground"
								onClick={() => setShowPassword((prev) => !prev)}
							>
								{showPassword
									? t("register.hidePassword")
									: t("register.showPassword")}
							</button>
						</div>
					</div>

					<Button
						type="submit"
						className="w-full"
						size="lg"
						disabled={isLoading || isRegistrationBusy}
					>
						{isLoading || isRegistrationBusy ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								{t("register.creating")}
							</>
						) : (
							t("register.createAccount")
						)}
					</Button>
					{/* <p className="text-center text-xs text-muted-foreground sm:text-sm">
						{t("register.commitmentNote")}
					</p> */}
				</form>

				<div className="w-full pt-1">
					<AltchaWidget widgetRef={widgetRef} hasActiveToken={hasActiveToken} />
				</div>

				<div className="text-center text-sm">
					<span className="text-muted-foreground">
						{t("register.alreadyHaveAccount")}{" "}
					</span>
					<Link to="/auth/login" className="hover:underline">
						{t("register.signIn")}
					</Link>
				</div>

				{/* Footer */}
				<div className="flex justify-center space-x-4 text-sm text-muted-foreground">
					<Link to="/agb">{t("footer.terms")}</Link>
					<Link to="/datenschutz">{t("footer.privacy")}</Link>
				</div>
			</div>
		</div>
	);
};

export default RegisterPage;
